import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import * as actions from "../../../../store/actions";
import Modal from "../../../../UI/Modal/Modal";
import SettingsItem from "./SettingsItem";
import SettingsNewItem from "./SettingsNewItem";
import Flex from "../../../../UI/Flex/Flex";
import EditRememberedSettings from "./EditRememberedSettings";
import useConfirmDelete from "../../../../hooks/useConfirmDelete";
import { Hr } from "../../DependencyFilter/FilterInputs";
import { useShallowEqualSelector } from "../../../../store";
import { SELECT_MULTIPLE_FILTERS } from "../../../../utils/constants/constants";
import Loading from "../../../../UI/Loading/Loading";

export default function RememberedSettings({
  close,
  filters,
  select,
  applyedGroup,
  setApplyedGroup,
  dateFilterType,
  dateFilters,
}) {
  const dispatch = useDispatch();
  const [group, setGroup] = useState({});

  const [editingGroup, setEditingGroup] = useState(null);

  const { groups, loadingUuid, loading, loadingOverride } =
    useShallowEqualSelector((state) => ({
      groups: state.layout.savedMenuFilterGroups,
      loadingUuid: state.layout.menuFilterGroupUuid,
      loading: state.layout.menuFilterGroupsLoading,
      loadingOverride: state.layout.menuFilterGroupOverride,
    }));

  useEffect(() => {
    if (!groups) {
      dispatch(actions.getSavedMenuFilterGroups());
    }
  }, [dispatch, groups]);

  const confirm = useConfirmDelete(
    (uuid) => deleteGroup(uuid),
    "Do you want to delete this filter group?",
    null,
    null,
    999999
  );

  function onClose() {
    setEditingGroup(null);
    close();
  }

  async function applyGroup(uuid) {
    try {
      const res = await dispatch(
        actions.getSavedMenuFilterGroup(uuid)
      ).unwrap();

      // drop filters checked items when switching between different groups
      select(res.data.data.menuFilterSettings, SELECT_MULTIPLE_FILTERS);
      setApplyedGroup(uuid);
    } catch (err) {
      throw new Error(err);
    }
  }

  // prepare all filters pairs
  const applyedFilters = filters.filter((filter) =>
    filter.values.some((v) => v.checked)
  );

  function getFiltersForSave() {
    return applyedFilters.map((filter) => ({
      menuFilterUuid: filter.uuid,
      values: filter.values.filter((v) => v.checked).map((v) => v.value),
    }));
  }

  function saveDates() {
    dispatch(actions.saveDateFilterSettings({ dateFilterType, dateFilters }));
  }

  function saveNewGroup() {
    const menuFilters = getFiltersForSave();

    dispatch(
      actions.createSavedMenuFilterGroup({ ...group, menuFilters }, () => {
        setGroup({});
        saveDates();
      })
    );
  }

  function updateGroup(group) {
    dispatch(
      actions.createSavedMenuFilterGroup(group, () => {
        setEditingGroup(null);
        saveDates();
      })
    );
  }

  async function deleteGroup(uuid) {
    await dispatch(actions.removeSavedMenuFilterGroup(uuid)).unwrap();
    dispatch(actions.getSavedMenuFilterGroups());
  }

  function dropDates() {
    dispatch(actions.dropSavedDateFilterSettings());
  }

  return (
    <>
      {confirm.confirming}
      <Modal title="Remembered Settings" width="45%" close={onClose}>
        {loading ? (
          <Flex height={300}>
            <Loading />
          </Flex>
        ) : (
          <Flex direction="column" height={350}>
            {editingGroup ? (
              <EditRememberedSettings
                uuid={editingGroup}
                filters={filters}
                setEditingGroup={setEditingGroup}
                updateGroup={updateGroup}
                loading={loadingUuid === editingGroup}
                overridenFilters={applyedFilters}
                loadingOverride={loadingOverride}
                Hr={Hr}
              />
            ) : (
              <>
                <SettingsNewItem
                  saveNewGroup={saveNewGroup}
                  group={group}
                  setGroup={setGroup}
                  loading={loadingUuid === "new"}
                  getFiltersForSave={getFiltersForSave}
                  dropDates={dropDates}
                />
                <Hr style={{ width: "96%", marginBottom: 20 }} />
                {groups?.map((item, index) => (
                  <SettingsItem
                    item={item}
                    key={item.uuid}
                    isActive={
                      // If there's no applied group — use the default group
                      applyedGroup ? applyedGroup === item.uuid : item.isDefault
                    }
                    applyGroup={applyGroup}
                    setEditingGroup={setEditingGroup}
                    loading={loadingUuid === item.uuid}
                    setDeletingGroup={confirm.setConfirming}
                  />
                ))}
              </>
            )}
          </Flex>
        )}
      </Modal>
    </>
  );
}
